<script setup lang="ts">
/**
 * ChangePasswordModal — current user changes their own password.
 *
 * Distinct from ResetPasswordModal (admin resetting someone else):
 * this requires the current password as confirmation. The backend's
 * AuthController.changeMyPassword endpoint validates the current
 * password against the stored hash and rejects with 401
 * INVALID_CREDENTIALS if it doesn't match.
 *
 * Three fields client-side: currentPassword, newPassword,
 * confirmPassword. Confirm is dropped before submitting; only
 * { currentPassword, newPassword } goes to the backend.
 */
import { watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import FormRow from '@/components/ui/FormRow.vue'
import { useApiForm } from '@/composables/useApiForm'
import { passwordChangeSchema } from '../schemas'
import { useChangeMyPasswordMutation } from '../queries'

const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()
const toast = useToastStore()
const change = useChangeMyPasswordMutation()

const form = useApiForm({
  schema: passwordChangeSchema,
  initialValues: {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  },
  submit: async (values) => {
    return change.mutateAsync({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    })
  },
  onSuccess: () => {
    toast.push({
      kind: 'success',
      message: t('account.changePassword.success'),
    })
    open.value = false
  },
  // The backend uses field name `currentPassword` exactly, so no
  // remap needed. If a future endpoint sent `oldPassword` instead,
  // this is where we'd remap.
})

watch(open, (isOpen) => {
  if (!isOpen) form.resetForm()
})

const [currentPassword] = form.defineField('currentPassword')
const [newPassword] = form.defineField('newPassword')
const [confirmPassword] = form.defineField('confirmPassword')

function close(): void {
  open.value = false
}
</script>

<template>
  <Modal
    v-model:open="open"
    :title="$t('account.changePassword.title')"
    :description="$t('account.changePassword.description')"
    size="md"
  >
    <form @submit.prevent="form.submit()">
      <FormRow
        :label="$t('account.changePassword.current.label')"
        :error="form.errors.value.currentPassword"
        required
      >
        <Input
          v-model="currentPassword"
          type="password"
          autocomplete="current-password"
          :error="form.errors.value.currentPassword"
        />
      </FormRow>

      <FormRow
        :label="$t('account.changePassword.new.label')"
        :helper="$t('account.changePassword.new.helper')"
        :error="form.errors.value.newPassword"
        required
      >
        <Input
          v-model="newPassword"
          type="password"
          autocomplete="new-password"
          :error="form.errors.value.newPassword"
        />
      </FormRow>

      <FormRow
        :label="$t('account.changePassword.confirm.label')"
        :error="form.errors.value.confirmPassword"
        required
      >
        <Input
          v-model="confirmPassword"
          type="password"
          autocomplete="new-password"
          :error="form.errors.value.confirmPassword"
        />
      </FormRow>
    </form>

    <template #footer>
      <Button kind="ghost" @click="close">
        {{ $t('actions.cancel') }}
      </Button>
      <Button
        kind="primary"
        type="submit"
        :loading="form.isSubmitting.value"
        @click="form.submit()"
      >
        {{ $t('account.changePassword.submit') }}
      </Button>
    </template>
  </Modal>
</template>
